import resultFilteringReducer from 'features/resultFiltering/resultFilteringSlice';
import searchMoleculeReducer from 'features/searchMolecules/searchMoleculesSlice';
import settingsReducer from 'features/settings/settingsSlice';
import shoppingCartReducer, { SupplierItem } from 'features/shoppingCart/shoppingCartSlice';
import workingQueryReducer from 'features/workingQuery/workingQuerySlice';
import throttle from 'lodash/throttle';
import { ThunkDispatch } from 'redux-thunk';
import LocalStorageService from 'services/LocalStorageService';

import {
  Action,
  combineReducers,
  configureStore,
  getDefaultMiddleware,
  ThunkAction,
} from '@reduxjs/toolkit';

const rootReducer = combineReducers({
  searchMolecules: searchMoleculeReducer,
  shoppingCart: shoppingCartReducer,
  settings: settingsReducer,
  workingQuery: workingQueryReducer,
  resultFiltering: resultFilteringReducer,
});

// Overall state of application to be used in mapStateToProps and selectors
export type RootState = ReturnType<typeof rootReducer>;

let preloadedState = LocalStorageService.loadState();

// This check fixes user that previously may have their state stuck
// in a perpetual loading state.
if (preloadedState !== undefined && preloadedState.searchMolecules !== undefined) {
  preloadedState.searchMolecules.isQueryLoading = false;
}

LocalStorageService.saveVersion();

export const store = configureStore({
  reducer: rootReducer,
  devTools: process.env.NODE_ENV !== 'production',
  preloadedState,
  middleware: getDefaultMiddleware(),
});

store.subscribe(
  throttle(() => {
    const state = store.getState();

    // Remove availability data from stored session as we need this to be clean
    const trimmedCarts = state.shoppingCart.carts.map((cart) => ({
      ...cart,
      items: cart.items.map((item) => ({
        ...item,
        availability: undefined,
        selectedItems: [] as SupplierItem[],
      })),
    }));

    LocalStorageService.saveState({
      ...state,
      workingQuery: undefined,
      shoppingCart: {
        ...state.shoppingCart,
        carts: trimmedCarts,
      },
    });
  }, 1000),
);

// The application dispatch type to be used in mapDispatchToProps
export type AppDispatch = typeof store.dispatch;
export type ReduxThunkDispatch = ThunkDispatch<RootState, unknown, Action<string>>;

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
